var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { memo } from 'react';
import RedeemOutlinedIcon from '@mui/icons-material/RedeemOutlined';
import { useCreateProjectDropMutation } from 'root/src/client/v2/drop/hooks/useCreateProjectDropMutation';
import { useRedesignModal } from 'root/src/client/v2/common/contexts/redesignModal.context';
import { useAsyncAction } from 'root/src/client/v2/common/hooks/useAsyncAction';
import { RedesignModalShell } from 'root/src/client/v2/common/components/RedesignModalShell';
import { Button } from 'root/src/client/v2/common/components/ui/button';
// A thrown-from-the-thunk error can be a legacy `{ error }` object (createProjectDrop) or a real Error.
const isLegacyError = (err) => (typeof err === 'object' && err !== null && 'error' in err);
// Redesign replacement for the legacy `NoMoreRewardModal`: a confirm dialog whose "Accept anyway"
// re-submits the accept without a reward. The retry runs through the same redesign path
// (`suppressErrorModals: true`) so any follow-up error surfaces as a sonner toast, not a legacy modal.
export const RedesignNoMoreRewardModal = memo(({ formData, onSuccess }) => {
    const modal = useRedesignModal();
    const createProjectDropMutation = useCreateProjectDropMutation();
    const { loading, success, error, run } = useAsyncAction();
    const onConfirm = () => {
        void run(() => __awaiter(void 0, void 0, void 0, function* () {
            yield createProjectDropMutation.mutateAsync(Object.assign(Object.assign({}, formData), { withoutReward: true, suppressErrorModals: true }));
        }), {
            // The success closes the modal, so no lingering tick.
            successHold: 0,
            onSuccess: () => {
                onSuccess === null || onSuccess === void 0 ? void 0 : onSuccess();
                modal.hide();
            },
            toast: {
                loading: 'Accepting dare...',
                success: 'Dare accepted',
                error: caught => (isLegacyError(caught) ? caught.error : caught instanceof Error ? caught.message : 'Dare cannot be accepted'),
            },
        });
    };
    return (<RedesignModalShell className="sm:max-w-md">
			<div className="flex w-full flex-col items-center gap-3 text-center">
				<div className="flex h-12 w-12 items-center justify-center rounded-full border border-solid border-neon-amber/30 bg-neon-amber/10">
					<RedeemOutlinedIcon sx={{ fontSize: 24 }} className="text-neon-amber"/>
				</div>
				<h2 className="font-display text-xl font-bold text-foreground">Rewards are gone</h2>
				<p className="text-sm text-muted-foreground">Do you still want to accept this dare despite that?</p>
				<p className="text-xs italic text-muted-foreground">
					If rewards are restocked, you will receive them as usual. If not, you will receive nothing.
				</p>
			</div>

			<div className="flex w-full gap-2">
				<Button variant="secondary" onClick={modal.hide} disabled={loading} className="flex-1 rounded-lg text-sm">
					Cancel
				</Button>
				<Button variant="brand" loading={loading} success={success} error={error} onClick={onConfirm} className="flex-1 rounded-lg text-sm">
					Accept anyway
				</Button>
			</div>
		</RedesignModalShell>);
});
